import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ODataFilterBuilder } from 'odata-filter-builder';
import { TableDatasource, DataTypes, Comparators } from '@gamelearn/skeletor/models';
import { DateTime, Duration } from 'luxon';
import { CountriesService } from './countries.service';
import {
  DatetimeQuestion,
  DropdownQuestion,
  NumberQuestion,
  QuestionBase,
  TextboxQuestion
} from 'core/components/forms';

@Injectable()
export class ListHelperService {
  constructor(
    public countries: CountriesService,
    public translate: TranslateService,
    public router: Router,
    private ngZone: NgZone
  ) {}

  parseDate(value: any, timezone?: any, format?: any): any {
    const lang = localStorage.getItem('backofficelanguage') || 'en';
    if (!value) {
      return '';
    }
    const dateTime = DateTime.fromISO(value, {
      zone: timezone
    })
      .setLocale(lang)
      .toLocaleString(format || DateTime.DATE_MED);

    return dateTime;
  }

  /**
   * Compare between the datetime and the datetime right now
   * @param datetime
   * @param timezone
   */
  compareDateTimeNow(datetime: Date | string, timezone: string): number {
    const tempDatetime = DateTime.fromISO(String(datetime), { zone: timezone });
    return tempDatetime.diffNow('milliseconds').toObject()['milliseconds'];
  }

  parsePlaytime(seconds) {
    const duration = Duration.fromMillis(seconds * 1000)
      .shiftTo('hours', 'minutes', 'seconds')
      .toFormat('hh:mm:ss');
    return duration;
  }

  parseCourseDuration(course) {
    const hoursLabel = course.hours > 0 ? this.translate.instant(course.hours > 1 ? 'nouns.hours' : 'nouns.hour') : '';
    const minutesLabel =
      course.minutes > 0 ? this.translate.instant(course.minutes > 1 ? 'nouns.minutes' : 'nouns.minute') : '';
    const hours = course.hours > 0 ? `${course.hours} ${hoursLabel} ` : '';
    const minutes = course.minutes > 0 ? `${course.minutes} ${minutesLabel}` : '';
    return `${hours} ${minutes}`;
  }

  parseCountryName(cca3: string): string {
    return this.countries.getCountryName(cca3);
  }

  parseAgent(agent) {
    if (agent) {
      return agent.fullName ? agent.fullName : agent.email;
    }
    return '';
  }

  filterColumns(columns, options) {
    return columns.filter((col) => {
      if (!options.disabledColumns) return true;
      return !options.disabledColumns.includes(col.source);
    });
  }

  getRouterLinkRenderer(renderer, url, value) {
    return renderer`
      <a href="${url}"
        @click="${(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          if (ev.shiftKey || ev.ctrlKey || ev.metaKey) {
            window.open(url, '_blank');
          } else {
            this.ngZone.run(() => {
              this.router.navigateByUrl(url).then();
            });
          }
        }}"
        title="${this.translate.instant('actions.edit')} ${value}"
      >
        ${value}
      </a>
    `;
  }

  public buildFilterString(filters, dataSource: TableDatasource, customFilters: any = {}): string {
    const $filter = ODataFilterBuilder();
    if (dataSource) {
      const columns = dataSource.columns;
      Object.keys(filters).forEach((key) => {
        // the '.' in nested fields must be replaced by '/' to be used in the filter
        const field = key.replaceAll('.', '/');
        const value = filters[key];
        const column = columns.find((col) => col.source === key);
        // don't filter null and undefined values but let use other 'falsy' values
        if (value != null) {
          if (customFilters[key]) {
            customFilters[key]($filter, value);
          } else {
            if (column) {
              switch (column.dataType) {
                case DataTypes.array: {
                  const colFilter = new ODataFilterBuilder('and');
                  value.forEach((v) => colFilter.contains(field, v));
                  $filter.and(colFilter);
                  break;
                }
                case DataTypes.boolean:
                case DataTypes.objectId:
                  $filter.eq(field, value);
                  break;
                case DataTypes.number:
                  this.createFilterExpression(column, field, value, $filter);
                  break;
                case DataTypes.datetime:
                  $filter.ge(field, value[0]).le(field, value[1]);
                  break;
                default:
                  if (value instanceof Array) {
                    const colFilter = new ODataFilterBuilder('or');
                    value.forEach((v) => colFilter.contains(field, v));
                    $filter.and(colFilter);
                  } else {
                    $filter.contains(field, value);
                  }
                  break;
              }
            }
          }
        }
      });
      if (customFilters['any']) {
        customFilters['any']($filter);
      }
    }
    return $filter.toString();
  }

  private createFilterExpression(column: any, field: string, value: any, $filter: any): void {
    switch (column.defaultComparator) {
      case Comparators.LESS_THAN.name:
        $filter.lt(field, value);
        break;
      case Comparators.GREATER_THAN.name:
        $filter.gt(field, value);
        break;
      default:
        $filter.eq(field, value);
        break;
    }
  }

  public navigateTo(route) {
    // eslint-disable-next-line
    this.ngZone.run(() => this.router.navigate(route));
  }

  public run(callback) {
    // eslint-disable-next-line
    this.ngZone.run(callback);
  }

  createQuestionFromColumn(column, questionParams: any = {}): QuestionBase<any> {
    if (!column.allowFiltering || !column.source) {
      return null;
    }
    const { source: key, label } = column;
    const questionData = {
      key,
      label
    };
    switch (column.dataType) {
      case DataTypes.boolean: {
        const options = [
          { key: 'any', value: this.translate.instant('properties.any') },
          { key: 'true', value: this.translate.instant('nouns.yes') },
          { key: 'false', value: this.translate.instant('nouns.no') }
        ];
        return new DropdownQuestion({
          ...questionData,
          options,
          value: 'any',
          ...questionParams
        });
      }
      case DataTypes.datetime: {
        return new DatetimeQuestion({
          ...questionData,
          timezone: 'utc',
          mode: 'range',
          validators: { invalidDate: true },
          ...questionParams
        });
      }
      case DataTypes.number:
        return new NumberQuestion({
          ...questionData,
          ...questionParams
        });
      default:
        return new TextboxQuestion({
          ...questionData,
          ...questionParams
        });
    }
  }
}
